import { Link } from '@remix-run/react';
import { Trash2, Pencil, CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Price } from '~/components/products/Price';
import { ActiveOrderQuery, CurrencyCode } from '~/generated/graphql';
import { CartActionGroup } from './CartActionGroup';
import EditScheduleModal from './EditScheduleModal';
import { calculateEffectivePrice } from '~/utils/pricing';

export function CartContents({
  orderLines,
  currencyCode,
  editable = true,
  adjustOrderLine,
  removeItem,
  onClose,
  showSavingsHint = false,
}: {
  orderLines: NonNullable<ActiveOrderQuery['activeOrder']>['lines'];
  currencyCode: CurrencyCode;
  editable?: boolean;
  adjustOrderLine?: (lineId: string, quantity: number) => void;
  removeItem?: (lineId: string) => void;
  onClose?: () => void;
  showSavingsHint?: boolean;
}) {
  const [editingLine, setEditingLine] = useState<any>(null);

  return (
    <div className="flow-root px-0.5 sm:px-6 lg:px-2">
      <ul role="list" className="space-y-2 sm:space-y-4">
        {orderLines?.map((line) => (
          <OrderLine
            key={line.id}
            line={line}
            currencyCode={currencyCode}
            editable={editable}
            removeItem={removeItem}
            adjustOrderLine={adjustOrderLine}
            onClose={onClose}
            showSavingsHint={showSavingsHint}
            onEdit={() => setEditingLine(line)}
          />
        ))}
      </ul>

      {editingLine && (
        <EditScheduleModal
          isOpen={!!editingLine}
          onClose={() => setEditingLine(null)}
          currencyCode={currencyCode}
          line={editingLine}
        />
      )}
    </div>
  );
}

type OrderLineProps = {
  line: NonNullable<ActiveOrderQuery['activeOrder']>['lines'][number];
  currencyCode: CurrencyCode;
  editable?: boolean;
  adjustOrderLine?: (lineId: string, quantity: number) => void;
  removeItem?: (lineId: string) => void;
  onClose?: () => void;
  showSavingsHint?: boolean;
  onEdit: () => void;
};

function OrderLine({
  line,
  currencyCode,
  editable,
  adjustOrderLine,
  removeItem,
  onClose,
  showSavingsHint,
  onEdit,
}: OrderLineProps) {
  const { t } = useTranslation();

  // @ts-ignore
  const { itemType, frequency, startDate, endDate } = line.customFields || {};
  const isScheduled = itemType === 'recurring' || itemType === 'future';

  return (
    <li
      className="group py-1 px-1 sm:py-3 sm:px-3 flex items-center gap-1 sm:gap-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-300 relative"
      aria-label={`Cart item: ${line.productVariant.name} `}
    >
      <div className="relative flex-shrink-0 w-10 h-10 sm:w-16 sm:h-16 rounded-md overflow-hidden bg-gray-50 shadow-sm">
        <img
          src={
            line.featuredAsset?.preview
              ? `${line.featuredAsset.preview}?preset=thumb`
              : '/brand_logo.png'
          }
          alt={line.productVariant.name}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${line.featuredAsset?.preview ? '' : 'opacity-50'}`}
          loading="lazy"
        />
      </div>

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex flex-col gap-0.5">
          <div className="flex justify-between items-start gap-2">
            <h3 className="text-[9px] sm:text-sm font-semibold text-gray-900 leading-tight line-clamp-1 flex-1 min-w-0">
              <Link
                to={`/products/${line.productVariant.product.slug}`}
                className="hover:text-primary transition-colors duration-300 focus:outline-none"
                onClick={() => onClose?.()}
              >
                {line.productVariant.name}
              </Link>
            </h3>

            {/* Schedule Summary */}
            {isScheduled && (
              <div className={`
                flex items-center gap-1.5 px-2 py-1 rounded-md w-fit text-[10px] sm:text-xs font-medium border shrink-0
                ${itemType === 'recurring'
                  ? 'bg-rose-50 text-rose-700 border-rose-100'
                  : 'bg-blue-50 text-blue-700 border-blue-100'}
              `}>
                {itemType === 'recurring' ? (
                  <>
                    <CalendarClock className="w-3 h-3 text-rose-500" />
                    <span className="capitalize">{frequency}</span>
                    <span className="text-rose-300">•</span>
                    <span>
                      {startDate ? new Date(startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'N/A'}
                      {' - '}
                      {endDate ? new Date(endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                    </span>
                  </>
                ) : (
                  <>
                    <CalendarClock className="w-3 h-3 text-blue-500" />
                    <span>Delivery: <strong>{startDate ? new Date(startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</strong></span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-0.5 text-[8px] sm:text-xs text-gray-600 whitespace-nowrap">
            <span className="font-semibold">
              <Price
                priceWithTax={line.proratedUnitPriceWithTax}
                currencyCode={currencyCode}
              />
            </span>
            <span className="text-gray-400">×</span>
            <span className="font-medium">{line.quantity}</span>
          </div>

          {/* Savings hint */}
          {showSavingsHint && (() => {
            const tiers = line.productVariant.customFields?.priceTiers;
            if (!tiers || tiers.length === 0) return null;
            const nextTier = tiers.find((t) => t.minQty && line.quantity < t.minQty);
            if (!nextTier || !nextTier.minQty || !nextTier.price) return null;
            const qtyNeeded = nextTier.minQty - line.quantity;
            const currentUnitPrice = line.proratedUnitPriceWithTax;
            const nextTierPrice = nextTier.price * 100;

            // Calculate Base Price for hint (Tax Inclusive)
            const basePrice = line.productVariant.priceWithTax || line.productVariant.price;
            const originalPrice = line.productVariant.customFields?.originalPrice
              ? line.productVariant.customFields.originalPrice
              : basePrice;

            // Calculate Current Savings
            const currentTotalSavings = (originalPrice - currentUnitPrice) * line.quantity;

            // Calculate Next Savings
            const nextTotalQty = line.quantity + qtyNeeded;
            const nextTotalSavings = (originalPrice - (nextTier.price || 0)) * nextTotalQty;

            // Calculate Difference
            const savingsToDisplay = nextTotalSavings - currentTotalSavings;

            if (savingsToDisplay <= 0) return null;
            return (
              <div className="text-xs text-[#E80245] font-medium mt-1">
                ⓘ Add {qtyNeeded} more to save{' '}
                <Price
                  priceWithTax={{ value: savingsToDisplay, __typename: 'SinglePrice' }}
                  currencyCode={currencyCode}
                  variant="primary"
                />{' '}
                in this order!
              </div>
            );
          })()}
        </div>

        <div className="flex flex-col gap-0.5 sm:gap-1">
          <div className="flex items-center justify-between">
            <div className="flex-1 opacity-100 bg-white"></div>
            <div className="flex flex-col items-end">
              {(() => {
                // Replicate exact PDP calculation logic from ProductOrderWidget.tsx
                // 1. Get base unit price (in minor units / paisa). Prefer priceWithTax (e.g. 550) over price (500)
                const baseUnitPriceValue = line.productVariant.priceWithTax || line.productVariant.price;

                // 2. Get price tiers
                const priceTiers = line.productVariant.customFields?.priceTiers;

                // 3. Calculate effective price using same utility as PDP
                const { effectiveUnitPrice } = calculateEffectivePrice(
                  baseUnitPriceValue,
                  priceTiers,
                  line.quantity,
                );

                // 4. Calculate total price (same as PDP)
                const totalPriceValue = effectiveUnitPrice * line.quantity;

                // 5. Get original price for savings calculation
                // In PDP: customOriginalPrice is used directly (assumed to be in minor units when set)
                // If not set, fall back to baseUnitPriceValue (which is now correctly tax-inclusive)
                const customOriginalPrice = line.productVariant.customFields?.originalPrice;
                const originalTotal = (customOriginalPrice ? customOriginalPrice : baseUnitPriceValue) * line.quantity;

                // 6. Calculate savings
                const savings = originalTotal - totalPriceValue;
                const savingsPercent = originalTotal > 0 ? Math.round((savings / originalTotal) * 100) : 0;

                if (savings > 0) {
                  return (
                    <div className="text-[10px] sm:text-xs text-[#E80245] font-medium mb-0.5">
                      -<Price priceWithTax={savings} currencyCode={currencyCode} variant="primary" /> ({savingsPercent}%)
                    </div>
                  );
                }
                return null;
              })()}
              <div className="text-[9px] sm:text-sm font-bold text-gray-900">
                <Price
                  priceWithTax={line.proratedLinePriceWithTax}
                  currencyCode={currencyCode}
                />
              </div>
            </div>
          </div>

          {editable && adjustOrderLine && !isScheduled && (
            <div className="flex items-center justify-between">
              <CartActionGroup
                editable={editable}
                lineId={line.id}
                lineQuantity={line.quantity}
                variantName={line.productVariant.name}
                adjustOrderLine={adjustOrderLine}
                removeItem={removeItem}
              />
            </div>
          )}
        </div>

        {editable && removeItem && (
          <div className="flex justify-end gap-2 absolute top-2 right-2 sm:static sm:mt-0">
            {isScheduled && (
              <button
                type="button"
                onClick={onEdit}
                className="flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white transition-all duration-200"
              >
                <Pencil className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                if (isScheduled && !window.confirm('Are you sure you want to cancel this plan? All future orders will be removed.')) return;
                try {
                  removeItem(line.id);
                } catch (error) { }
              }}
              aria-label={t('cart.removeItem')}
              className="flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 rounded-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white active:scale-95 transition-all duration-200"
            >
              <Trash2 className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
